import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../SEO";
import TurnstileWidget from "./TurnstileWidget";
import HexacoSamplePdfButtons from "./HexacoSamplePdfButtons";
import {
  HexacoInvalidPdfAlertDialog,
  HexacoRateLimitAlertDialog,
  HexacoValidPdfGuideModal,
} from "./HexacoInvalidPdfDialogs";
import { HexacoPurchaseLocationState } from "./hexacoPurchaseNavigation";
import HexacoServiceTermsModal from "./HexacoServiceTermsModal";
import {
  HEXACO_OFFICIAL_TEST_URL,
  HEXACO_REPORT_PRICE_HKD,
  HEXACO_UPLOAD_MAX_BYTES,
  HEXACO_UPLOAD_MAX_MB,
} from "../../constants/hexaco";
import {
  createOrder,
  HexacoWebApiError,
  uploadPdf,
} from "../../services/hexacoWebApi";
import {
  getApiErrorMessage,
  getRateLimitErrorMessage,
  isInvalidHexacoPdfError,
  isRateLimitError,
  looksLikePdf,
} from "./hexacoPurchaseUtils";

type InvalidPdfFlowState = "closed" | "alert" | "guide";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const HexacoPurchasePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [tncAccepted, setTncAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidPdfFlow, setInvalidPdfFlow] =
    useState<InvalidPdfFlowState>("closed");
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as HexacoPurchaseLocationState | null;
    if (state?.showInvalidPdfAlert) {
      setInvalidPdfFlow("alert");
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
    setError(null);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    setError(t("hexaco.purchase.errors.turnstile"));
  }, [t]);

  const isEmailValid = EMAIL_PATTERN.test(customerEmail.trim());

  const validateEmail = (value = customerEmail): string | null => {
    const email = value.trim();
    if (!email) {
      return t("hexaco.purchase.fields.email.required");
    }
    if (!EMAIL_PATTERN.test(email)) {
      return t("hexaco.purchase.fields.email.invalid");
    }
    return null;
  };

  const emailErrorMessage = emailTouched ? validateEmail() : null;

  const canUpload = tncAccepted && Boolean(turnstileToken) && isEmailValid && !uploading;

  const handleUploadClick = () => {
    setEmailTouched(true);
    if (!canUpload) {
      return;
    }

    const emailError = validateEmail();
    if (emailError) {
      setError(emailError);
      return;
    }

    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError(t("hexaco.purchase.errors.invalidFile"));
      return;
    }

    if (file.size > HEXACO_UPLOAD_MAX_BYTES) {
      setError(
        t("hexaco.purchase.errors.fileTooLarge", { maxMb: HEXACO_UPLOAD_MAX_MB }),
      );
      return;
    }

    if (!(await looksLikePdf(file))) {
      setError(t("hexaco.purchase.errors.invalidFile"));
      return;
    }

    const emailError = validateEmail();
    if (emailError) {
      setError(emailError);
      return;
    }

    if (!turnstileToken) {
      setError(t("hexaco.purchase.errors.turnstileRequired"));
      return;
    }

    setUploading(true);
    setError(null);
    setInvalidPdfFlow("closed");
    setRateLimitMessage(null);

    try {
      const language = i18n.resolvedLanguage || i18n.language;
      const { orderId, accessToken } = await createOrder({
        language,
        tncAccepted: true,
        customerEmail: customerEmail.trim(),
        customerName: customerName.trim() || undefined,
      });

      await uploadPdf(orderId, accessToken, file, turnstileToken);
      navigate(`/hexaco/order/${orderId}`);
    } catch (uploadError) {
      if (isRateLimitError(uploadError)) {
        setRateLimitMessage(getRateLimitErrorMessage(uploadError, t));
        setError(null);
      } else if (
        uploadError instanceof HexacoWebApiError &&
        isInvalidHexacoPdfError(
          uploadError.message,
          uploadError.businessStatus,
        )
      ) {
        setInvalidPdfFlow("alert");
        setError(null);
      } else {
        setError(
          getApiErrorMessage(uploadError, t, {
            maxUploadMb: HEXACO_UPLOAD_MAX_MB,
          }),
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const closeRateLimitAlert = useCallback(() => {
    setRateLimitMessage(null);
  }, []);

  const closeErrorAlert = useCallback(() => {
    setError(null);
  }, []);

  const dismissInvalidPdfAlert = useCallback(() => {
    setInvalidPdfFlow("closed");
  }, []);

  const openInvalidPdfGuide = useCallback(() => {
    setInvalidPdfFlow("guide");
  }, []);

  const closeInvalidPdfGuide = useCallback(() => {
    setInvalidPdfFlow("closed");
  }, []);

  return (
    <>
      <SEO
        title={t("hexaco.purchase.seo.title")}
        description={t("hexaco.purchase.seo.description")}
        keywords={t("hexaco.purchase.seo.keywords")}
        canonical="https://deepsoulai.com/hexaco/purchase"
      />

      <Box bg="gray.50" py={{ base: 10, md: 14 }}>
        <Container maxW="720px">
          <VStack align="stretch" spacing={8}>
            <Box textAlign="center">
              <Text fontSize="3xl" mb={3}>
                📄
              </Text>
              <Heading size="lg" color="gray.800" mb={3}>
                {t("hexaco.purchase.title")}
              </Heading>
              <Text color="gray.600" lineHeight="tall" mb={2}>
                {t("hexaco.purchase.subtitleBefore")}
                <Link
                  href={HEXACO_OFFICIAL_TEST_URL}
                  isExternal
                  color="teal.600"
                  fontWeight="semibold"
                  textDecoration="underline"
                >
                  {t("hexaco.purchase.subtitleLink")}
                </Link>
                {t("hexaco.purchase.subtitleAfter")}
              </Text>
              <Text color="gray.600" lineHeight="tall" mb={2}>
                {t("hexaco.purchase.priceHint", {
                  price: HEXACO_REPORT_PRICE_HKD,
                })}
              </Text>
              <Text color="gray.600" lineHeight="tall">
                {t("hexaco.purchase.sampleHint")}
              </Text>
            </Box>

            <Box
              bg="white"
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor="teal.100"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 8 }}
            >
              <VStack align="stretch" spacing={6}>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700" mb={2}>
                    {t("hexaco.purchase.fields.name.label")}
                  </FormLabel>
                  <Input
                    value={customerName}
                    onChange={(event) => setCustomerName(event.target.value)}
                    placeholder={t("hexaco.purchase.fields.name.placeholder")}
                    maxLength={128}
                    bg="white"
                  />
                  <FormHelperText>
                    {t("hexaco.purchase.fields.name.hint")}
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(emailErrorMessage)}>
                  <FormLabel fontSize="sm" color="gray.700" mb={2}>
                    {t("hexaco.purchase.fields.email.label")}
                  </FormLabel>
                  <Input
                    type="text"
                    inputMode="email"
                    value={customerEmail}
                    onChange={(event) => setCustomerEmail(event.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    placeholder={t("hexaco.purchase.fields.email.placeholder")}
                    maxLength={255}
                    bg="white"
                    autoComplete="email"
                    required={false}
                  />
                  <FormHelperText>
                    {t("hexaco.purchase.fields.email.hint")}
                  </FormHelperText>
                  {emailErrorMessage && (
                    <FormErrorMessage>{emailErrorMessage}</FormErrorMessage>
                  )}
                </FormControl>

                <Checkbox
                  isChecked={tncAccepted}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setTncAccepted(checked);
                    if (!checked) {
                      setTurnstileToken(null);
                    }
                  }}
                  colorScheme="teal"
                  alignItems="flex-start"
                >
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    {t("hexaco.purchase.terms.prefix")}{" "}
                    <Link
                      as="button"
                      type="button"
                      color="teal.600"
                      fontWeight="semibold"
                      textDecoration="underline"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setTermsModalOpen(true);
                      }}
                    >
                      {t("hexaco.purchase.terms.link")}
                    </Link>
                  </Text>
                </Checkbox>

                {tncAccepted && (
                  <>
                    <Divider borderColor="gray.200" />

                    <Box>
                      <Text fontSize="sm" color="gray.600" mb={3}>
                        {t("hexaco.purchase.turnstileHint")}
                      </Text>
                      <TurnstileWidget
                        onVerify={handleTurnstileVerify}
                        onExpire={handleTurnstileExpire}
                        onError={handleTurnstileError}
                      />
                    </Box>
                  </>
                )}

                <Button
                  colorScheme="teal"
                  size="lg"
                  borderRadius="full"
                  onClick={handleUploadClick}
                  isDisabled={!canUpload}
                  isLoading={uploading}
                  loadingText={t("hexaco.purchase.upload.uploading")}
                >
                  {t("hexaco.purchase.upload.button")}
                </Button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  onChange={handleFileChange}
                />

                <HexacoSamplePdfButtons
                  language={i18n.resolvedLanguage || i18n.language}
                />
              </VStack>
            </Box>

            <Text fontSize="sm" color="gray.500" textAlign="center">
              {t("hexaco.purchase.priceNotice")}
            </Text>
          </VStack>
        </Container>
      </Box>

      <HexacoServiceTermsModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
      <HexacoRateLimitAlertDialog
        isOpen={error !== null}
        message={error || t("hexaco.purchase.errors.generic")}
        onClose={closeErrorAlert}
      />
      <HexacoRateLimitAlertDialog
        isOpen={rateLimitMessage !== null}
        message={rateLimitMessage || t("hexaco.purchase.errors.rateLimit")}
        onClose={closeRateLimitAlert}
      />
      <HexacoInvalidPdfAlertDialog
        isOpen={invalidPdfFlow === "alert"}
        onClose={dismissInvalidPdfAlert}
        onOpenGuide={openInvalidPdfGuide}
      />
      <HexacoValidPdfGuideModal
        isOpen={invalidPdfFlow === "guide"}
        onClose={closeInvalidPdfGuide}
      />
    </>
  );
};

export default HexacoPurchasePage;
